import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, Shield, Wallet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_stake-blc/artifacts/qx16sddk_Screenshot__247_-removebg-preview%20(1).png";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const sliderRef = useRef(null);

  const handleSliderStart = (e) => {
    e.preventDefault();
    const slider = sliderRef.current;
    if (!slider) return;

    const startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const sliderRect = slider.parentElement.getBoundingClientRect();
    const maxX = sliderRect.width - 44;

    const handleMove = (moveEvent) => {
      const currentX = moveEvent.type === 'touchmove' ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const deltaX = currentX - startX;
      const newPos = Math.max(0, Math.min(deltaX, maxX));
      setSliderPosition(newPos);

      if (newPos >= maxX - 10) {
        setCaptchaVerified(true);
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      }
    };

    const handleEnd = () => {
      if (!captchaVerified) {
        setSliderPosition(0);
      }
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleEnd);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      toast.error('Please verify the captcha');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Login successful!');
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-400 flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,_rgba(212,175,55,0.1)_0%,_transparent_50%)] pointer-events-none" />

      <div className="relative z-10 w-full max-w-[480px] px-6 py-12">
        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center mb-8"
        >
          <div className="relative w-48 h-32 flex items-center justify-center mb-2">
            <div className="absolute inset-0 bg-amber-500/10 rounded-full blur-2xl animate-pulse" />
            <img src={LOGO_MAIN} alt="STAKE BLC" className="h-28 relative z-10 object-contain" />
          </div>
        </motion.div>

        {/* Login Form */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-2xl p-8 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
          
          <div className="text-center mb-8">
            <h2 className="text-xl text-white font-normal">Welcome Back</h2>
            <p className="text-xs text-zinc-500 mt-1">Secure access to your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-xs text-zinc-400 ml-1">Email Address</label>
              <div className="input-glass rounded-lg flex items-center px-3 py-3 gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="9" r="3"/>
                  <circle cx="12" cy="12" r="10"/>
                  <path strokeLinecap="round" d="M17.97 20c-.16-2.892-1.045-5-5.97-5s-5.81 2.108-5.97 5"/>
                </svg>
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email" 
                  className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                  data-testid="login-email"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center ml-1">
                <label className="text-xs text-zinc-400">Password</label>
                <a href="#" className="text-[10px] text-amber-500/80 hover:text-amber-400 transition-colors">
                  Forgot Password?
                </a>
              </div>
              <div className="input-glass rounded-lg flex items-center px-3 py-3 gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M2 16c0-2.828 0-4.243.879-5.121C3.757 10 5.172 10 8 10h8c2.828 0 4.243 0 5.121.879C22 11.757 22 13.172 22 16s0 4.243-.879 5.121C20.243 22 18.828 22 16 22H8c-2.828 0-4.243 0-5.121-.879C2 20.243 2 18.828 2 16Z"/>
                  <path strokeLinecap="round" d="M6 10V8a6 6 0 1 1 12 0v2"/>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16h.009m3.982 0H12m3.991 0H16"/>
                </svg>
                <input 
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" 
                  className="bg-transparent border-none outline-none text-sm text-white w-full placeholder-zinc-600"
                  data-testid="login-password"
                  required
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Slider Captcha */}
            <div className="space-y-1.5 pt-2">
              <div className="slider-captcha-container relative" data-testid="captcha-slider">
                {!captchaVerified && (
                  <div className="slider-text">SLIDE TO VERIFY</div>
                )}
                <div 
                  className="slider-track" 
                  style={{ width: captchaVerified ? '100%' : `${sliderPosition + 44}px` }}
                />
                <div 
                  ref={sliderRef}
                  className={`slider-handle ${captchaVerified ? 'verified' : ''}`}
                  style={{ left: captchaVerified ? 0 : `${sliderPosition}px`, width: captchaVerified ? '100%' : '44px' }}
                  onMouseDown={!captchaVerified ? handleSliderStart : undefined}
                  onTouchStart={!captchaVerified ? handleSliderStart : undefined}
                >
                  {captchaVerified ? (
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Shield className="w-4 h-4" /> Verified
                    </span>
                  ) : (
                    <ArrowRight className="w-5 h-5" />
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2 space-y-3">
              <button 
                type="submit" 
                disabled={loading || !captchaVerified}
                className="w-full py-3 rounded-lg gold-btn flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    Secure Login <Shield className="w-4 h-4" />
                  </>
                )}
              </button>
              
              <button 
                type="button" 
                className="w-full py-3 rounded-lg border border-white/10 hover:bg-white/5 transition-all text-zinc-300 text-sm flex items-center justify-center gap-2 group"
              >
                <Wallet className="w-4 h-4 text-zinc-500 group-hover:text-amber-400 transition-colors" />
                Connect Wallet
              </button>
            </div>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-amber-400 transition-colors">
              Forgot your password?
            </Link>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-zinc-500">
              Don't have an account? {' '}
              <Link to="/signup" className="text-amber-400 hover:text-amber-300 font-medium transition-colors" data-testid="signup-link">
                Join via Referral
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
